import React, { useState, useEffect } from 'react';
import { Modal, Input, Switch, Select, Tag, Button, Empty, Spin, message as antdMessage } from 'antd';
import { BellOutlined, UserOutlined, TagOutlined, ExclamationCircleOutlined, UsergroupAddOutlined, CloseCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { $api } from 'services/service';
import userAnnouncements from 'services/userAnnouncements.service';

const { TextArea } = Input;
const { Option } = Select;

interface NotificationModalProps {
    visible: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const NotificationModal: React.FC<NotificationModalProps> = ({ visible, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [messageContent, setMessageContent] = useState('');
    const [enforceRead, setEnforceRead] = useState(false);
    const [notificationType, setNotificationType] = useState('all');
    const [announcementType, setAnnouncementType] = useState(2); // Default to 'Dịch vụ'
    const [studentSearch, setStudentSearch] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [selectedStudents, setSelectedStudents] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    // Debounced search for students
    useEffect(() => {
        if (notificationType === 'custom' && studentSearch.trim()) {
            const timer = setTimeout(() => {
                handleSearchStudents(studentSearch);
            }, 500);
            return () => clearTimeout(timer);
        } else {
            setSearchResults([]);
        }
    }, [studentSearch, notificationType]);

    const handleSearchStudents = async (searchText: string) => {
        if (!searchText.trim()) {
            setSearchResults([]);
            return;
        }
        setIsSearching(true);
        try {
            const res: any = await $api.student.getPaging({
                freeTextSearch: searchText,
                pageSize: 20,
                pageIndex: 1
            });
            if (res && res.result && res.result.items) {
                setSearchResults(res.result.items);
            }
        } catch (e) {
            console.error('Search error:', e);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const toggleStudentSelection = (student: any) => {
        const isSelected = selectedStudents.some((s) => s.id === student.id);
        if (isSelected) {
            setSelectedStudents(selectedStudents.filter((s) => s.id !== student.id));
        } else {
            setSelectedStudents([...selectedStudents, student]);
        }
    };

    const addAllStudents = () => {
        const newSelected = [...selectedStudents];
        searchResults.forEach((student: any) => {
            if (!newSelected.some((s) => s.id === student.id)) {
                newSelected.push(student);
            }
        });
        setSelectedStudents(newSelected);
        antdMessage.success(`Đã thêm ${searchResults.length} sinh viên`);
    };

    const removeStudent = (studentId: string) => {
        setSelectedStudents(selectedStudents.filter((s) => s.id !== studentId));
    };

    const handleSubmit = async () => {
        if (!messageContent.trim()) {
            antdMessage.error('Vui lòng nhập nội dung thông báo');
            return;
        }

        if (notificationType === 'custom' && selectedStudents.length === 0) {
            antdMessage.error('Vui lòng chọn ít nhất một sinh viên');
            return;
        }

        Modal.confirm({
            title: 'Xác nhận gửi thông báo',
            icon: <ExclamationCircleOutlined />,
            content: notificationType === 'all'
                ? 'Bạn muốn gửi thông báo đến toàn bộ sinh viên?'
                : `Bạn muốn gửi thông báo đến ${selectedStudents.length} sinh viên đã chọn?`,
            okText: 'Gửi',
            cancelText: 'Hủy',
            onOk: async () => {
                setLoading(true);
                try {
                    const requestData: any = {
                        message: messageContent,
                        enforceRead: enforceRead,
                        notificationType: announcementType
                    };

                    if (notificationType === 'custom') {
                        requestData.studentIds = selectedStudents.map((s) => s.id);
                    } else {
                        requestData.studentIds = null;
                    }

                    const res: any = await userAnnouncements.createNotification(requestData);
                    if (res) {
                        antdMessage.success(res.message || 'Gửi thông báo thành công');
                        handleClose();
                        onSuccess?.();
                    }
                } catch (error: any) {
                    const errMsg = error?.response?.data?.errors?.exceptionMessage?.title
                        || error?.response?.data?.errors?.exceptionMessage
                        || 'Không thể gửi thông báo';
                    antdMessage.error(errMsg);
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleClose = () => {
        setMessageContent('');
        setEnforceRead(false);
        setNotificationType('all');
        setAnnouncementType(2);
        setSelectedStudents([]);
        setStudentSearch('');
        setSearchResults([]);
        onClose();
    };

    return (
        <Modal
            title={
                <div className="flex items-center gap-2 text-lg">
                    <BellOutlined className="text-blue-600" />
                    <span>Gửi thông báo sinh viên</span>
                </div>
            }
            open={visible}
            onCancel={handleClose}
            width={700}
            footer={[
                <Button key="cancel" icon={<CloseCircleOutlined />} onClick={handleClose}>
                    Hủy
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    icon={<BellOutlined />}
                    loading={loading}
                    onClick={handleSubmit}
                >
                    Gửi thông báo
                </Button>
            ]}
        >
            <div className="space-y-4">
                {/* Important notification toggle */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <ExclamationCircleOutlined className="text-yellow-600 text-lg" />
                        <span className="font-medium text-yellow-800">Thông báo quan trọng</span>
                    </div>
                    <Switch
                        checked={enforceRead}
                        onChange={setEnforceRead}
                        checkedChildren="Bật"
                        unCheckedChildren="Tắt"
                    />
                </div>

                {/* Announcement type */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        <TagOutlined className="mr-1" /> Loại thông báo
                    </label>
                    <Select
                        value={announcementType}
                        onChange={setAnnouncementType}
                        className="w-full"
                        size="large"
                    >
                        <Option value={1}>
                            <Tag color="green">Hoạt động</Tag>
                        </Option>
                        <Option value={2}>
                            <Tag color="orange">Dịch vụ</Tag>
                        </Option>
                        <Option value={3}>
                            <Tag color="blue">Hệ thống</Tag>
                        </Option>
                    </Select>
                </div>

                {/* Message content */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        <BellOutlined className="mr-1" /> Nội dung thông báo
                    </label>
                    <TextArea
                        rows={5}
                        placeholder="Nhập nội dung thông báo..."
                        value={messageContent}
                        onChange={(e) => setMessageContent(e.target.value)}
                        showCount
                        maxLength={1000}
                    />
                </div>

                {/* Recipient selection */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        <UserOutlined className="mr-1" /> Gửi đến
                    </label>
                    <Select
                        value={notificationType}
                        onChange={(value) => {
                            setNotificationType(value);
                            setStudentSearch('');
                            setSearchResults([]);
                            setSelectedStudents([]);
                        }}
                        className="w-full"
                        size="large"
                    >
                        <Option value="all">
                            <UsergroupAddOutlined className="mr-1" /> Tất cả sinh viên
                        </Option>
                        <Option value="custom">
                            <UserOutlined className="mr-1" /> Tùy chọn sinh viên
                        </Option>
                    </Select>
                </div>

                {/* Custom student selection */}
                {notificationType === 'custom' && (
                    <div className="space-y-3 border-t pt-4">
                        <div className="flex gap-2">
                            <Input.Search
                                placeholder="Tìm kiếm sinh viên (tên, email, mã SV...)"
                                value={studentSearch}
                                onChange={(e) => setStudentSearch(e.target.value)}
                                size="large"
                                loading={isSearching}
                            />
                            {searchResults.length > 0 && (
                                <Button
                                    type="primary"
                                    icon={<CheckCircleOutlined />}
                                    onClick={addAllStudents}
                                    size="large"
                                >
                                    Thêm tất cả
                                </Button>
                            )}
                        </div>

                        {/* Search results */}
                        {studentSearch.trim() && (
                            <div className="border border-gray-300 rounded-lg max-h-60 overflow-y-auto">
                                {isSearching ? (
                                    <div className="p-6 text-center">
                                        <Spin tip="Đang tìm kiếm..." />
                                    </div>
                                ) : searchResults.length > 0 ? (
                                    <div className="divide-y">
                                        {searchResults.map((student: any) => {
                                            const isSelected = selectedStudents.some((s) => s.id === student.id);
                                            return (
                                                <div
                                                    key={student.id}
                                                    className={`p-3 cursor-pointer hover:bg-gray-50 flex items-center justify-between transition-colors ${
                                                        isSelected ? 'bg-blue-50' : ''
                                                    }`}
                                                    onClick={() => toggleStudentSelection(student)}
                                                >
                                                    <div>
                                                        <div className="font-medium text-gray-900">{student.fullName}</div>
                                                        <div className="text-sm text-gray-500">
                                                            {student.userName} - {student.userInternalId}
                                                        </div>
                                                    </div>
                                                    {isSelected && (
                                                        <CheckCircleOutlined className="text-blue-600 text-xl" />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description="Không tìm thấy sinh viên"
                                        className="py-6"
                                    />
                                )}
                            </div>
                        )}

                        {/* Selected students */}
                        {selectedStudents.length > 0 && (
                            <div>
                                <div className="text-sm font-medium text-gray-700 mb-2">
                                    <UsergroupAddOutlined className="mr-1" />
                                    Sinh viên đã chọn ({selectedStudents.length})
                                </div>
                                <div className="border border-gray-300 rounded-lg max-h-48 overflow-y-auto p-2 bg-gray-50">
                                    <div className="space-y-2">
                                        {selectedStudents.map((student: any) => (
                                            <div
                                                key={student.id}
                                                className="flex items-center justify-between bg-white p-3 rounded-lg shadow-sm"
                                            >
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900">{student.fullName}</div>
                                                    <div className="text-xs text-gray-500">{student.userName}</div>
                                                </div>
                                                <Button
                                                    type="text"
                                                    danger
                                                    icon={<CloseCircleOutlined />}
                                                    onClick={() => removeStudent(student.id)}
                                                    size="small"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default NotificationModal;
